import React from 'react';
import { QuestionField } from '../QuestionField';
import type { ScreenQuestion } from '../../../features/flow/types';
import {
  evaluateShowIf,
  isPngFileName,
  normalizeFileAnswerValueForKey,
  prettyAnswerKey,
  type UploadedFileRow,
} from '../../../features/flow/utils';

export type ReviewSection = { name: string; items: Array<[string, unknown]> };

export function NonResidentReviewScreenBody({
  editableAnswersSections,
  uploadedFilesForReview,
  questions,
  answers,
  answersForShowIf,
  cleanedScreenTitle,
  sessionScreenId,
  actionBusy,
  setAnswers,
}: {
  editableAnswersSections: ReviewSection[];
  uploadedFilesForReview: UploadedFileRow[];
  questions: ScreenQuestion[];
  answers: Record<string, unknown>;
  answersForShowIf: Record<string, any>;
  cleanedScreenTitle: string;
  sessionScreenId: string;
  actionBusy: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}) {
  return (
    <div className="w-full flex flex-col gap-10">
      <div className="w-full flex flex-col gap-10">
        {editableAnswersSections.map((section, idx) => (
          <div key={section.name} className="w-full">
            {idx > 0 ? <div className="w-full border-t border-[#E2E8F0] pt-10" /> : null}
            <div className="w-full max-w-[668px] pb-3">
              <div className="text-base font-medium text-[#131315]">{section.name}:</div>
            </div>

            <div className="mt-6 w-full max-w-[668px] grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 items-start">
              {section.items.map(([k, v]) => {
                const label = prettyAnswerKey(k);
                const valStr = v === null || v === undefined ? '' : String(v);
                const isNum = typeof v === 'number';
                const isArr = Array.isArray(v);
                return (
                  <div key={k} className="min-w-0 col-span-1">
                    <div className="flex flex-col gap-3">
                      <label className="block text-sm text-[#131315] font-medium">{label}</label>
                      {isArr ? (
                        <textarea
                          className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]"
                          value={JSON.stringify(v)}
                          onChange={(e) => {
                            const raw = e.target.value;
                            try {
                              const parsed = JSON.parse(raw);
                              setAnswers((prev) => ({ ...prev, [k]: parsed }));
                            } catch {
                              setAnswers((prev) => ({ ...prev, [k]: raw }));
                            }
                          }}
                          rows={3}
                        />
                      ) : (
                        <input
                          className="w-full rounded-lg border border-[#E2E8F0] px-4 py-2 text-[#131315] bg-white focus:outline-none focus:ring-2 focus:ring-[#1066CB]/20 focus:border-[#1066CB]"
                          type={isNum ? 'number' : 'text'}
                          value={valStr}
                          onChange={(e) =>
                            setAnswers((prev) => ({
                              ...prev,
                              [k]: isNum
                                ? e.target.value === ''
                                  ? ''
                                  : Number(e.target.value)
                                : e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {uploadedFilesForReview.length > 0 ? (
        <div className="w-full border-t border-[#E2E8F0] pt-10">
          <div className="w-full max-w-[668px] pb-3">
            <div className="text-sm font-semibold text-[#131315]">Uploaded Files:</div>
          </div>

          <div className="mt-4 w-full max-w-[668px] flex flex-col gap-3">
            {uploadedFilesForReview.map((row, i) => (
              <div key={`${row.answerKey}_${row.fileName}_${row.fileIndex}`}>
                <div className="text-xs text-[#525353] mb-2">{row.sectionName}</div>
                <div className="w-full max-w-[310px] rounded-lg border border-[#E2E8F0] bg-white px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-[#131315] break-all">
                    File {i + 1}: {row.fileName}
                  </span>
                  <button
                    type="button"
                    disabled={actionBusy}
                    onClick={() => {
                      setAnswers((prev) => {
                        const curVal = prev[row.answerKey];
                        const curAll = normalizeFileAnswerValueForKey(row.answerKey, curVal);
                        let pngSeen = -1;
                        const nextAll = curAll.filter((ref) => {
                          if (!isPngFileName(ref.name)) return true;
                          pngSeen += 1;
                          if (pngSeen === row.fileIndex && ref.name === row.fileName) return false;
                          return true;
                        });
                        return { ...prev, [row.answerKey]: nextAll.length > 0 ? nextAll : null };
                      });
                    }}
                    className="text-sm font-semibold text-[#131315] disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="w-full border-t border-[#E2E8F0] pt-8">
        <div className="w-full max-w-[668px] flex flex-col gap-4">
          {questions.map((q) => {
            const shouldShow = evaluateShowIf(q.show_if, answersForShowIf);
            if (!shouldShow) return null;
            return (
              <div key={q.id} className="w-full">
                <QuestionField
                  q={q}
                  answers={answers}
                  setAnswers={setAnswers}
                  cleanedScreenTitle={cleanedScreenTitle}
                  sessionScreenId={sessionScreenId}
                  actionBusy={actionBusy}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
